import { useEffect, useState, useCallback } from "react";
import {
  supabase,
  type ProductItem,
  type ProductCategory,
  type ProductVariant,
  type ProductSku,
} from "@/lib/supabase";
import {
  mockCategories,
  mockProducts,
  mockVariants,
  mockSkus,
} from "@/mocks/catalog";

// Sempre usa dados locais (mock) enquanto o catálogo do backend não está configurado em português.
// Para usar o backend real, remova a linha abaixo e defina backendReady via env.
const backendReady = false;

interface UseProductsResult {
  products: ProductItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

interface UseFeaturedProductsOptions {
  limit?: number;
  categoryId?: number | null;
  excludeId?: number | null;
}

export function useFeaturedProducts(
  limitOrOptions: number | UseFeaturedProductsOptions = 8,
): UseProductsResult {
  const opts: UseFeaturedProductsOptions =
    typeof limitOrOptions === "number" ? { limit: limitOrOptions } : limitOrOptions;
  const { limit = 8, categoryId = null, excludeId = null } = opts;

  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!backendReady) {
      let list = [...(mockProducts as ProductItem[])];
      if (categoryId != null) {
        list = list.filter((p) => p.category_id === categoryId);
      }
      if (excludeId != null) {
        list = list.filter((p) => p.id !== excludeId);
      }
      setProducts(list.slice(0, limit));
      setLoading(false);
      return;
    }

    try {
      let query = supabase
        .from("product_items")
        .select("*, product_categories(id, name, sort_order)")
        .eq("status", "active");

      if (categoryId != null) {
        query = query.eq("category_id", categoryId);
      }
      if (excludeId != null) {
        query = query.neq("id", excludeId);
      }

      const { data, error: err } = await query
        .order("created_at", { ascending: false })
        .limit(limit);

      if (err) throw err;
      setProducts((data ?? []) as ProductItem[]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Não foi possível carregar os produtos";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [limit, categoryId, excludeId]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return { products, loading, error, refetch: fetchProducts };
}

interface UseCategoriesResult {
  categories: ProductCategory[];
  loading: boolean;
  error: string | null;
}

export function useCategories(): UseCategoriesResult {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    if (!backendReady) {
      setCategories([...(mockCategories as ProductCategory[])]);
      setLoading(false);
      return () => {
        alive = false;
      };
    }

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from("product_categories")
          .select("*")
          .order("sort_order");
        if (err) throw err;
        if (alive) setCategories((data ?? []) as ProductCategory[]);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Não foi possível carregar as categorias";
        if (alive) setError(msg);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return { categories, loading, error };
}

interface UseProductResult {
  product: ProductItem | null;
  loading: boolean;
  error: string | null;
}

export function useProduct(id: number | string | null): UseProductResult {
  const [product, setProduct] = useState<ProductItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id == null || id === "") {
      setProduct(null);
      setLoading(false);
      return;
    }
    let alive = true;

    if (!backendReady) {
      const numeric = Number(id);
      const found =
        (mockProducts as ProductItem[]).find((p) => p.id === numeric) ?? null;
      setProduct(found);
      setLoading(false);
      return () => {
        alive = false;
      };
    }

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from("product_items")
          .select("*, product_categories(id, name, sort_order)")
          .eq("id", id)
          .maybeSingle();
        if (err) throw err;
        if (alive) setProduct((data ?? null) as ProductItem | null);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Não foi possível carregar este produto";
        if (alive) setError(msg);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  return { product, loading, error };
}

interface UseProductVariantsResult {
  variants: ProductVariant[];
  skus: ProductSku[];
  loading: boolean;
  error: string | null;
}

export function useProductVariants(productId: number | null): UseProductVariantsResult {
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [skus, setSkus] = useState<ProductSku[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (productId == null) {
      setVariants([]);
      setSkus([]);
      setLoading(false);
      return;
    }
    let alive = true;

    if (!backendReady) {
      setVariants(
        (mockVariants as ProductVariant[]).filter((v) => v.product_id === productId),
      );
      setSkus((mockSkus as ProductSku[]).filter((s) => s.product_id === productId));
      setLoading(false);
      return () => {
        alive = false;
      };
    }

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [variantsRes, skusRes] = await Promise.all([
          supabase
            .from("product_variants")
            .select("*")
            .eq("product_id", productId)
            .order("sort_order"),
          supabase
            .from("product_skus")
            .select("*")
            .eq("product_id", productId),
        ]);
        if (variantsRes.error) throw variantsRes.error;
        if (skusRes.error) throw skusRes.error;
        if (alive) {
          setVariants((variantsRes.data ?? []) as ProductVariant[]);
          setSkus((skusRes.data ?? []) as ProductSku[]);
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Não foi possível carregar as opções";
        if (alive) setError(msg);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [productId]);

  return { variants, skus, loading, error };
}

interface UseProductsByIdsResult {
  products: ProductItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useProductsByIds(ids: number[]): UseProductsByIdsResult {
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const idsKey = ids.slice().sort((a, b) => a - b).join(",");

  const fetchByIds = useCallback(async () => {
    if (ids.length === 0) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    if (!backendReady) {
      setProducts(
        (mockProducts as ProductItem[]).filter((p) => ids.includes(p.id)),
      );
      setLoading(false);
      return;
    }

    try {
      const { data, error: err } = await supabase
        .from("product_items")
        .select("*, product_categories(id, name, sort_order)")
        .in("id", ids);
      if (err) throw err;
      setProducts((data ?? []) as ProductItem[]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Não foi possível carregar os favoritos";
      setError(msg);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  useEffect(() => {
    fetchByIds();
  }, [fetchByIds]);

  return { products, loading, error, refetch: fetchByIds };
}

interface UseVariantsByProductIdsResult {
  variantsByProduct: Record<number, ProductVariant[]>;
  loading: boolean;
  error: string | null;
}

export function useVariantsByProductIds(ids: number[]): UseVariantsByProductIdsResult {
  const [variantsByProduct, setVariantsByProduct] = useState<Record<number, ProductVariant[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const idsKey = ids.slice().sort((a, b) => a - b).join(",");

  useEffect(() => {
    if (ids.length === 0) {
      setVariantsByProduct({});
      setLoading(false);
      return;
    }
    let alive = true;

    if (!backendReady) {
      const grouped: Record<number, ProductVariant[]> = {};
      for (const v of mockVariants as ProductVariant[]) {
        if (ids.includes(v.product_id)) {
          const arr = grouped[v.product_id] ?? [];
          arr.push(v);
          grouped[v.product_id] = arr;
        }
      }
      setVariantsByProduct(grouped);
      setLoading(false);
      return () => {
        alive = false;
      };
    }

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from("product_variants")
          .select("*")
          .in("product_id", ids)
          .order("sort_order");
        if (err) throw err;
        if (alive) {
          const grouped: Record<number, ProductVariant[]> = {};
          for (const v of (data ?? []) as ProductVariant[]) {
            const arr = grouped[v.product_id] ?? [];
            arr.push(v);
            grouped[v.product_id] = arr;
          }
          setVariantsByProduct(grouped);
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Não foi possível carregar as variações";
        if (alive) setError(msg);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return { variantsByProduct, loading, error };
}